<template>
  <div class="app-container">
    <!-- 登录页直接显示，不显示其他组件 -->
    <router-view v-if="isLoginPage"></router-view>

    <!-- 非登录页显示完整布局 -->
    <div v-else class="layout-body">
      <!-- 顶部栏 -->
      <HeaderComponent
        :is-desktop="isDesktop"
        @toggle-menu="toggleMenu"
      />

      <!-- 主体内容区 -->
      <div class="main-wrapper">
        <!-- 左侧菜单 -->
        <MenuComponent
          :is-desktop="isDesktop"
          :is-menu-open="isMenuOpen"
          @menu-click="handleMenuClick"
        />

        <!-- 右侧主内容 -->
        <main class="app-main" :class="{ 'main-collapsed': !isMenuOpen && !isDesktop }">
          <!-- 电脑端Tab标签 -->
          <div class="tab-container" v-if="isDesktop">
            <div class="tab-bar">
              <div
                v-for="tab in tabs"
                :key="tab.path"
                class="tab-item"
                :class="{ 'active': activeTab === tab.path }"
                @click="switchTab(tab)"
              >
                <span class="tab-title">{{ tab.name }}</span>
                <span
                  class="tab-close"
                  @click.stop="closeTab(tab)"
                >×</span>
              </div>
            </div>
            <div class="tab-content">
              <router-view></router-view>
            </div>
          </div>
          <!-- 手机端直接显示内容 -->
          <div v-else>
            <router-view></router-view>
          </div>
        </main>
      </div>
    </div>

    <!-- 全局底部版权（非登录页） -->
    <FooterComponent v-if="!isLoginPage" />
  </div>
</template>

<script>
import HeaderComponent from './components/HeaderComponent.vue'
import MenuComponent from './components/MenuComponent.vue'
import FooterComponent from './components/FooterComponent.vue'

export default {
  name: 'App',
  components: {
    HeaderComponent,
    MenuComponent,
    FooterComponent
  },
  data() {
    return {
      isDesktop: true,
      isMenuOpen: true,
      // Tab相关数据
      tabs: [],
      activeTab: '',
      // 路由信息映射（扩展为包含所有主要路由）
      routeMap: {
        '/home': { name: '工作台' },
        '/monitor': { name: '实时监控' },
        '/factory': { name: '厂区管理' },
        '/device': { name: '设备管理' },
        '/security': { name: '安全管理' },
        '/alert': { name: '告警中心' },
        '/data-center': { name: '数据中心' },
        '/asset': { name: '设备资产' },
        '/track': { name: '轨迹回放' },
        '/behavior': { name: '行为分析' },
        '/audit': { name: '审计日志' },
        '/message': { name: '消息中心' },
        '/report': { name: '报表中心' },
        '/workorder': { name: '运维工单' },
        '/mine': { name: '账号管理' }
      }
    }
  },
  computed: {
    // 判断是否为登录页
    isLoginPage() {
      return this.$route.path === '/login'
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
    // 初始化默认tab（非登录页）
    if (this.$route.path !== '/login') {
      this.addTab(this.$route)
    }
  },
  watch: {
    // 监听路由变化，添加新tab
    '$route': {
      handler(to, from) {
        // 登录页不添加tab
        if (to.path !== '/login') {
          this.addTab(to)
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      const width = window.innerWidth
      const wasDesktop = this.isDesktop
      this.isDesktop = width > 768
      // 仅在桌面/手机跨端切换时重置菜单展开状态，桌面端允许手动折叠
      if (wasDesktop !== this.isDesktop) {
        this.isMenuOpen = width > 768
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    // 添加tab
    addTab(route) {
      const path = route.path
      let name = '未命名页面'
      
      // 尝试从routeMap中获取名称
      if (this.routeMap[path] && this.routeMap[path].name) {
        name = this.routeMap[path].name
      } else {
        // 如果routeMap中没有定义，尝试从路由配置中获取名称
        const matchedRoute = this.$router.options.routes.find(r => r.path === path)
        if (matchedRoute && matchedRoute.name) {
          name = matchedRoute.name
        }
      }
      
      // 检查tab是否已存在
      const existingTab = this.tabs.find(tab => tab.path === path)
      if (!existingTab) {
        this.tabs.push({
          path: path,
          name: name
        })
      }
      // 设置当前激活的tab
      this.activeTab = path
    },
    // 切换tab
    switchTab(tab) {
      this.activeTab = tab.path
      this.$router.push(tab.path)
    },
    // 关闭tab
    closeTab(tab) {
      // 不允许关闭最后一个tab
      if (this.tabs.length <= 1) return
      
      const index = this.tabs.indexOf(tab)
      this.tabs.splice(index, 1)
      
      // 如果关闭的是当前激活的tab，切换到前一个或后一个tab
      if (this.activeTab === tab.path) {
        const newActiveIndex = index > 0 ? index - 1 : 0
        const newActiveTab = this.tabs[newActiveIndex]
        this.switchTab(newActiveTab)
      }
    },
    // 处理菜单点击事件，在手机端点击菜单后关闭菜单
    handleMenuClick() {
      this.isMenuOpen = false
    }
  }
}
</script>

<style scoped>
/* app-container 固定整屏高度，禁止被内容撑开（否则 body 出现整页滚动条） */
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background-color: var(--background);
}

/* 主布局体：占满剩余高度（扣除底部版权条），min-height:0 允许收缩 */
.layout-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* 主体内容区布局 */
.main-wrapper {
  display: flex;
  flex: 1;
  min-height: 0;
  margin-top: 64px; /* 为顶部栏留出空间 */
  overflow: hidden; /* 隐藏主内容区的滚动条 */
}

/* 主内容区样式 */
.app-main {
  flex: 1;
  padding: 0;
  background-color: var(--bg-200);
  transition: margin-left 0.3s ease;
}

/* Tab标签样式 */
.tab-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

/* Tab栏样式（现代化：白底 + 胶囊激活态） */
.tab-bar {
  display: flex;
  align-items: center;
  background-color: #fff;
  border-bottom: 1px solid var(--border);
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
  padding: 0 8px;
  gap: 4px;
}

/* Webkit滚动条样式 */
.tab-bar::-webkit-scrollbar {
  height: 4px;
}

.tab-bar::-webkit-scrollbar-track {
  background: transparent;
}

.tab-bar::-webkit-scrollbar-thumb {
  background-color: var(--border);
  border-radius: 2px;
}

.tab-bar::-webkit-scrollbar-thumb:hover {
  background-color: var(--border-dark);
}

/* Tab项样式 */
.tab-item {
  display: flex;
  align-items: center;
  padding: 7px 14px;
  margin: 7px 0;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
  font-size: 13px;
  color: var(--text-200);
}

.tab-item:hover {
  background-color: var(--light);
  color: var(--text);
}

/* 激活的Tab项样式 */
.tab-item.active {
  background-color: var(--primary-100);
  color: var(--primary);
  font-weight: 600;
}

/* Tab标题样式 */
.tab-title {
  margin-right: 8px;
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.tab-item.active .tab-title {
  font-weight: 600;
}

/* Tab关闭按钮样式 */
.tab-close {
  font-size: var(--font-size-xs);
  color: var(--text-300);
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tab-close:hover {
  opacity: 1;
  color: var(--danger);
  background-color: rgba(245, 63, 63, 0.1);
}

/* Tab内容区样式 */
.tab-content {
  flex: 1;
  padding: 15px;
  padding-bottom:0;
  overflow-y: auto;
  background-color: var(--bg-200);
}

/* 电脑端样式 */
@media (min-width: 769px) {
  .app-main {
    margin-left: 0;
    overflow: hidden; /* 电脑端隐藏主内容区滚动条 */
    padding: 12px;
    box-sizing: border-box;
    background-color: var(--background);
    border-radius: 16px;
  }

  .tab-container{
    height: 100%;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }
  
  .main-collapsed {
    margin-left: 0;
  }
}

/* 手机端样式 */
@media (max-width: 768px) {
  .app-main {
    position: absolute;
    top: 64px;
    height: calc(100% - 64px);
    left: 0;
    right: 0;
    bottom: 0;
    transform: translateX(240px); /* 与手机端菜单宽度一致 */
    transition: transform 0.3s ease;
    padding: 15px;
    padding-top: 0;
    z-index: 998;
    overflow-y: auto; /* 手机端允许垂直滚动 */
  }
  
  .main-collapsed {
    transform: translateX(0);
  }
}
</style>